import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, ArrowLeft, Loader2, Zap } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VoteButton from "../components/VoteButton";
import ReleaseModal from "../components/ReleaseModal";
import { usePublicAuth } from "../context/PublicAuthContext";
import api from "../utils/api";

function ReleaseFavoriteCard({ release, onRemove }) {
  const [modalOpen, setModalOpen] = useState(false);
  const year = release.erscheinungsdatum?.split("-")[0] || "";

  return (
    <>
      <div
        onClick={() => setModalOpen(true)}
        className="group relative cursor-pointer rounded-2xl overflow-hidden bg-zinc-900/50 border border-white/[0.05] hover:border-white/10 transition-all duration-200 hover:shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
      >
        {/* Cover */}
        <div className="aspect-square overflow-hidden bg-zinc-800">
          {release.cover_url
            ? <img src={release.cover_url} alt={release.titel} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            : <div className="w-full h-full flex items-center justify-center"><Bookmark size={24} className="text-zinc-700" /></div>
          }
        </div>
        {/* Info */}
        <div className="p-3">
          <p className="text-white text-sm font-bold truncate">{release.interpret}</p>
          <p className="text-zinc-500 text-xs truncate">{release.titel}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-zinc-700 text-xs">{year}</span>
            <div className="flex items-center gap-2">
              {/* Chart-Vote (Herz) */}
              <VoteButton release={release} size={12} showCount className="text-xs" />
              {/* Aus Gespeichert entfernen (Bookmark) */}
              <button
                onClick={e => { e.stopPropagation(); onRemove(release.id); }}
                className="text-amber-500/70 hover:text-amber-300 transition-colors p-1"
                title="Aus Gespeichert entfernen"
              >
                <BookmarkCheck size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {modalOpen && <ReleaseModal release={release} onClose={() => setModalOpen(false)} />}
    </>
  );
}

export default function UserFavoritesPage() {
  const { publicUser, isPublicAuth, publicApi, isPublicLoading } = usePublicAuth();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPublicLoading || !isPublicAuth) { setLoading(false); return; }
    const load = async () => {
      try {
        const favRes = await publicApi("get", "/me/favorites");
        const ids = favRes.data.map(f => f.item_id).filter(Boolean);
        if (!ids.length) { setLoading(false); return; }
        const releases = await Promise.all(
          ids.map(id => api.get(`/releases/by-id/${id}`).then(r => r.data).catch(() => null))
        );
        setReleases(releases.filter(Boolean));
      } catch { /* silent */ } finally { setLoading(false); }
    };
    load();
  }, [isPublicAuth, isPublicLoading]); // eslint-disable-line

  const handleRemove = async (releaseId) => {
    try {
      await publicApi("delete", `/me/favorites/${releaseId}`);
      setReleases(prev => prev.filter(r => r.id !== releaseId));
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        {/* Back + Title */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/profil" className="text-zinc-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              <Bookmark size={20} className="text-amber-500" fill="currentColor" /> Gespeichert
            </h1>
            {!loading && <p className="text-zinc-600 text-sm mt-0.5">{releases.length} gespeicherte Releases</p>}
          </div>
        </div>

        {/* Not logged in */}
        {!isPublicLoading && !isPublicAuth && (
          <div className="text-center py-20">
            <p className="text-zinc-500 mb-4">Du musst angemeldet sein.</p>
            <Link to="/" className="text-rose-400 hover:text-rose-300 text-sm transition-colors">← Startseite</Link>
          </div>
        )}

        {/* Not supporter */}
        {!isPublicLoading && isPublicAuth && !publicUser?.is_supporter && (
          <div className="rounded-2xl bg-rose-500/5 border border-rose-500/15 p-8 text-center">
            <Zap size={32} className="text-rose-400 mx-auto mb-3" />
            <h2 className="text-white font-bold text-lg mb-2">Gespeichert ist ein Supporter-Feature</h2>
            <p className="text-zinc-500 text-sm mb-5">Speichere unbegrenzt Releases – ab 3€/Monat.</p>
            <Link to="/supporter" className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm transition-colors">
              Jetzt Supporter werden
            </Link>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-zinc-600" /></div>
        )}

        {/* Empty */}
        {!loading && isPublicAuth && publicUser?.is_supporter && releases.length === 0 && (
          <div className="text-center py-20">
            <Bookmark size={40} className="text-zinc-800 mx-auto mb-4" />
            <p className="text-zinc-500">Noch nichts gespeichert.</p>
            <Link to="/search" className="mt-4 inline-block text-rose-400 hover:text-rose-300 text-sm transition-colors">
              Releases entdecken →
            </Link>
          </div>
        )}

        {/* Grid */}
        {!loading && releases.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {releases.map(r => (
              <ReleaseFavoriteCard key={r.id} release={r} onRemove={handleRemove} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
